import type { adminAr } from './admin.ar';

/** Administration copy (English). Must cover exactly the same keys as `adminAr`. */
export const adminEn: typeof adminAr = {
  notify: {
    emptyNone: 'No notifications sent yet.',
    nav: 'Notifications',
    title: 'Notifications',
    description:
      "Send a push notification to one agent, a selection, a team or everyone. It also goes to each agent's in-app list, so nothing is lost if a push is missed. Every send is recorded in the audit log.",
    compose: 'New notification',
    audience: 'Send to',
    audiences: { USERS: 'Selected agents', TEAM: 'A team', EVERYONE: 'Everyone' },
    agents: 'Agents',
    team: 'Team of',
    titleAr: 'Title (Arabic)',
    titleEn: 'Title (English, optional)',
    bodyAr: 'Message (Arabic)',
    bodyEn: 'Message (English, optional)',
    deepLink: 'Opens in the app',
    deepLinkNone: 'The notifications list',
    targets: {
      home: 'Home',
      tasks: 'My tasks',
      pending: 'Sync',
      performance: 'My visits',
      settings: 'Settings',
      notifications: 'Notifications list',
      assignment: 'An assignment',
    },
    preview: 'Preview',
    previewTitle: 'Check before sending',
    previewCounts: '{{recipients}} recipients · {{reachable}} with a registered phone ({{devices}} phones) · {{muted}} muted announcements',
    previewMuted: 'Agents who muted announcements get it in the in-app list only.',
    send: 'Send',
    sent: 'Notification sent',
    history: 'History',
    sentBy: 'Sent by',
    recipients: 'Recipients',
    delivery: 'Delivery',
    deliveries: 'Delivery per phone',
    kind: { ASSIGNMENT: 'Assignment', ANNOUNCEMENT: 'Announcement', TEST: 'Test' },
    statuses: {
      QUEUED: 'Queued',
      SENT: 'Sent',
      DELIVERED: 'Delivered to the platform',
      FAILED: 'Failed',
      NO_DEVICE: 'No registered phone',
      MUTED: 'Muted',
      NOT_SENT: 'Not sent',
    },
    read: 'Read',
    platform: 'Platform',
    reason: 'What happened',
    audienceUsers: '{{count}} people',
    audienceUsers_one: '{{count}} person',
    audienceUsers_two: '{{count}} people',
    audienceUsers_few: '{{count}} people',
    audienceTeam: 'Team of {{name}}',
    audienceTest: 'Test: {{name}}',
    device: 'Phone',
    testSelf: 'Send test notification to myself',
    testSend: 'Send the test',
    testAgain: 'Send again',
    testHint:
      'Sends a test notification right now to every phone where you are signed in to the mobile app. Close the app on the phone first: if the notification appears, delivery works.',
    testWaiting: 'Waiting for the delivery receipt from Google/Apple…',
    testNoReceipt: 'No receipt yet. Expo can take a few minutes; open this test again from the history to see the final result.',
    testOutcomes: { DELIVERED: 'Delivered', SENT: 'Sent, waiting', FAILED: 'Failed', NO_TOKEN: 'No token' },
  },
  env: {
    development: 'Development',
    staging: 'Staging',
    production: 'Production',
  },
  status: {
    api: 'API',
    apiOk: 'Connected',
    apiDegraded: 'Degraded',
    apiDown: 'Unreachable',
    database: 'Database',
    queue: 'Sync worker',
    up: 'up',
    down: 'down',
    disabled: 'disabled',
    zoho: 'Zoho',
    zohoConnected: 'Connected',
    zohoNotConfigured: 'Not configured',
    zohoTestFailed: 'Last test failed',
    zohoUntested: 'Not tested',
  },
  zoho: {
    title: 'Zoho Desk integration',
    description: 'OAuth connection and ticket settings the sync worker uses for every Zoho Desk call.',
    configuration: 'Configuration',
    connection: 'Connection',
    connectionSubtitle: 'Last connection test',
    dataCenter: 'Data center',
    clientId: 'Client ID',
    clientSecret: 'Client secret',
    storedSecret: 'Stored encrypted – leave empty to keep',
    orgId: 'Organization ID',
    departmentId: 'Department ID',
    layoutId: 'Layout ID (optional)',
    ticketStatus: 'New ticket status',
    ticketChannel: 'Ticket channel',
    contactStrategy: 'Ticket contact',
    contactFixed: 'One fixed contact for all visits',
    contactFromForm: 'Find or create from form fields',
    fixedContactId: 'Fixed contact ID',
    contactNameField: 'Form field with contact name',
    contactPhoneField: 'Form field with contact phone',
    redirectUri: 'Redirect URI to register in the Zoho API console',
    connect: 'Connect to Zoho',
    reconnect: 'Reconnect',
    disconnect: 'Disconnect',
    disconnectTitle: 'Disconnect Zoho Desk?',
    disconnectConsequence: 'Visits stop syncing to Zoho Desk. They are kept and wait until you connect again.',
    testTicketTitle: 'Create a test ticket?',
    connected: 'OAuth connected',
    notConnected: 'Not connected – visits are stored but not synced',
    test: 'Test connection',
    testOk: 'Last test passed',
    testFailed: 'Last test failed',
    notTested: 'Not tested',
    testTicket: 'Create test ticket',
    testTicketHint: 'Creates a ticket marked TEST in Zoho Desk using the active form and current mappings.',
    testTicketCreated: 'Test ticket {{number}} created',
    sentPayload: 'Sent payload',
    oauthSuccess: 'Zoho Desk connected.',
    oauthError: 'Zoho Desk connection failed: {{reason}}',
    saveFirst: 'Save the client ID and secret before connecting.',
    lastTest: 'Last test',
    lastTestMessage: 'Last test result',
    lastTestReason: 'Why it failed', // new — needs native review
    testFailedReason: 'Connection test failed: {{reason}}', // new — needs native review
    apiBaseUrl: 'API address',
    /** New: the OAuth callback's reason codes in words (the previous app showed the code). */
    oauthReasons: {
      STATE_INVALID: 'The connection link is invalid or was already used. Start connecting again.',
      STATE_EXPIRED: 'The connection link expired. Start connecting again.',
      ACCESS_DENIED: 'Access was denied in Zoho.',
      MISSING_CODE: 'Zoho returned no authorization code.',
      NOT_CONFIGURED: 'Save the client ID and secret before connecting.',
      DATA_CENTER_MISMATCH: 'The chosen data center does not match the Zoho account.',
      NO_REFRESH_TOKEN: "Zoho returned no refresh token. Remove the app's access in the Zoho account, then connect again.",
      EXCHANGE_FAILED: 'Exchanging the code with Zoho failed. Check the client ID and secret.',
    },
    /** New: the ticket layout's fields, as the form builder maps to them. */
    ticketFields: 'Ticket fields',
    ticketFieldsHint: 'The fields of the Zoho Desk ticket layout that form fields can be mapped to.',
    loadTicketFields: 'Load from Zoho',
    ticketFieldsNeedConnection: 'Save the configuration and connect to Zoho to load ticket fields.',
    fieldApiName: 'API name',
    fieldType: 'Type',
    fieldMandatory: 'Required',
    fieldCustom: 'Custom',
    allowedValues: 'Allowed values',
    fetchedAt: 'Fetched',
  },
  distribution: {
    title: 'Mobile app distribution',
    description: 'Upload the Android build here, then send agents the link or the QR code.',
    notConfigured: 'No build has been uploaded yet.',
    status: {
      hosted: 'v{{version}} published · install link active',
      external: 'No build hosted here · agents install from the external link',
      revokedExternal: 'v{{version}} link revoked · agents install from the external link',
      revokedNoLink: 'v{{version}} link revoked · agents have no install link',
      none: 'No build and no install link yet',
    },
    published: 'Published build',
    upload: 'Upload a new build',
    settings: 'Distribution settings',
    builds: 'Builds kept on the server',
    version: 'Version',
    size: 'Size',
    uploadedAt: 'Uploaded',
    uploadedBy: 'Uploaded by',
    releaseNotes: 'Release notes',
    apkFile: 'APK file',
    chooseApk: 'Choose the APK',
    releaseNotesPlaceholder: 'e.g. Bug fixes and performance improvements',
    apkFileHint: 'Android .apk only, up to {{max}}. The file is checked before it is published.',
    uploadAndPublish: 'Upload and publish',
    uploading: 'Uploading…',
    keepsLastBuilds: 'The server keeps the last {{count}} builds and deletes older ones.',
    keepsLastBuilds_one: 'The server keeps only the latest build and deletes older ones.',
    keepsLastBuilds_two: 'The server keeps the last {{count}} builds and deletes older ones.',
    keepsLastBuilds_few: 'The server keeps the last {{count}} builds and deletes older ones.',
    downloadLink: 'Download link',
    openLandingPage: 'Open the install page',
    copyLink: 'Copy link',
    scanToInstall: 'Scan with the agent’s phone to install',
    linkRevoked: 'The public link is revoked. Rotate it to share this build again.',
    rotateLink: 'Rotate link',
    revokeLink: 'Revoke link',
    revokeTitle: 'Revoke the install link?',
    revokeConsequence: 'The current link and QR code stop working at once. Agents cannot install from them until you rotate the link.',
    rotateTitle: 'Rotate the install link?',
    rotateConsequence: 'A new link and QR code replace the current ones, and the old link stops working. Send agents the new one.',
    publishTitle: 'Publish v{{version}}?',
    publishConsequence: 'The install link and QR code serve this build from now on, instead of the build published now.',
    deleteBuildTitle: 'Delete v{{version}}?',
    deleteBuildConsequence:
      'The build file is removed from the server. If it is the published build, agents have no hosted install link until you publish another. This cannot be undone.',
    publishThis: 'Publish this build',
    state: 'State',
    statePublished: 'Published',
    stateAvailable: 'Available',
    stateRevoked: 'Link revoked',
    permissionRequired: 'You need the "Manage app distribution" permission to change this.',
    bandwidthWarning:
      'Every install costs about {{size}} of bandwidth from this server. That is fine for a small team; once there are many agents, host the APK on a cloud drive or Play and use the external link below instead.',
    minimumVersion: 'Minimum supported version',
    minimumVersionHint: 'Below this, the app refuses to submit visits and asks the agent to update. Leave empty for no floor.',
    externalUrl: 'External install link (optional)',
    externalUrlHint: 'Used instead of the hosted APK when no build is published here — for example Play or a cloud drive.',
    externalIosUrl: 'iPhone (TestFlight) link',
    externalIosUrlHint:
      'The iPhone app’s TestFlight install link; shown on the install page for agents with iPhones. Leave empty if there is no iOS build.',
    versionsInUse: 'App versions in use',
    versionsInUseHint: 'Derived from the app version sent with each submission over the last 90 days.',
    noVersionsSeen: 'No submissions in the last 90 days.',
    unknownVersion: 'Not reported',
    agents: 'Agents',
    visits: 'Visits',
    lastSeen: 'Last seen',
    serverEndpoints: 'Server Connection & IP Discovery',
    publicIp: 'Public WAN IP',
    localIp: 'Local LAN IP',
    useIp: 'Use {{label}} {{ip}}',
    publicIpNeedsHttps: 'a public address needs https (a domain or a tunnel); the app build refuses plain http to it.',
    targetApiUrl: 'Server API Base URL',
    targetApiUrlHint: 'The exact URL the mobile app will connect to on agents phones. Ensure it is accessible.',
    testConnection: 'Test Endpoint Reachability',
    endpointReachable: 'Endpoint is reachable and healthy (latency: {{ms}} ms)',
    endpointUnreachable: 'Could not reach endpoint: {{reason}}',
    shareWhatsApp: 'Share on WhatsApp',
    whatsAppShareMessage: 'Download Field Support Mobile App (v{{version}}): {{url}}',
    /** New: the server health card and the technical detail of a failed check. */
    health: 'Server health',
    apiVersion: 'API version',
    technicalDetail: 'Technical detail',
    /** Why an address check failed (ENDPOINT_CHECK_REASONS), inside endpointUnreachable. */
    endpointReason: {
      INVALID_URL: 'the address is not valid', // new — needs native review
      UNSUPPORTED_PROTOCOL: 'the address must start with http:// or https://', // new — needs native review
      CREDENTIALS_IN_URL: 'remove the user name and password from the address', // new — needs native review
      ADDRESS_NOT_ALLOWED: 'this address is not allowed (local or reserved)', // new — needs native review
      DNS_FAILED: 'the host name was not found (DNS)', // new — needs native review
      TIMEOUT: 'no answer in time', // new — needs native review
      CONNECTION_REFUSED: 'connection refused: nothing is listening on that port', // new — needs native review
      TLS_ERROR: 'the secure connection failed (certificate expired, self-signed or for another name)', // new — needs native review
      UNREACHABLE: 'the address cannot be reached', // new — needs native review
      HTTP_REDIRECT: 'the server answered with a redirect (HTTP {{statusCode}}); use the final address', // new — needs native review
      HTTP_ERROR: 'the server answered with an error (HTTP {{statusCode}})', // new — needs native review
    },
  },
  gallery: {
    title: 'Component gallery',
    description: 'Every UI component in every state, in Arabic (RTL) and English (LTR). Development builds only.',
  },
};
